import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useDealEventRepository } from '../../../rpc';
import { useTaskRepository, useTaskStatusRepository } from '@ayocore/tasks/client/rpc';
import { useCommentRepository } from '@ayocore/comments/client/rpc';
import { DealEventController } from './DealEventController';

interface DealRef {
  id: number;
  uuid: string;
}

export function useDealEventController(deal: DealRef) {
  const controllerRef = useRef<DealEventController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new DealEventController();
  }
  const controller = controllerRef.current;

  const dealEventRepo = useDealEventRepository();
  const commentRepo = useCommentRepository();
  const taskRepo = useTaskRepository();
  const taskStatusRepo = useTaskStatusRepository();

  const dealEventRepoRef = useRef(dealEventRepo);
  const commentRepoRef = useRef(commentRepo);
  const taskRepoRef = useRef(taskRepo);
  const taskStatusRepoRef = useRef(taskStatusRepo);

  useEffect(() => {
    dealEventRepoRef.current = dealEventRepo;
    commentRepoRef.current = commentRepo;
    taskRepoRef.current = taskRepo;
    taskStatusRepoRef.current = taskStatusRepo;
  }, [dealEventRepo, commentRepo, taskRepo, taskStatusRepo]);

  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot);

  useEffect(() => {
    controller.load(deal.id, deal.uuid, {
      dealEventRepo: dealEventRepoRef.current,
      commentRepo: commentRepoRef.current,
      taskRepo: taskRepoRef.current,
      taskStatusRepo: taskStatusRepoRef.current,
    });

    return () => {
      controller.clear();
    };
  }, [deal.id, deal.uuid, controller]);

  return {
    events: state.events,
    comments: state.comments,
    activeTasks: state.activeTasks,
    loading: state.loading,
    error: state.error,
    reload: () =>
      controller.reload(deal.id, deal.uuid, {
        dealEventRepo: dealEventRepoRef.current,
        commentRepo: commentRepoRef.current,
        taskRepo: taskRepoRef.current,
        taskStatusRepo: taskStatusRepoRef.current,
      }),
  };
}
